import {
  AddAPhotoOutlined,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Stack,
  type SvgIconProps,
  Typography,
} from '@mui/material';
import { type ComponentType, type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiDisclaimerBlock } from '../../../../components/ai-disclaimer-block';
import { ExtractionUsageCounter } from '../../../../components/extraction-usage-counter';
import { MAX_FILE_SIZE_MB } from '../../../../config/upload';
import { colors } from '../../../../theme/ai-theme';

type UploadOptionVariant = 'ai' | 'plain';

interface UploadOptionCardProps {
  variant: UploadOptionVariant;
  isEditMode: boolean;
  aiQuotaExhausted?: boolean;
  busy?: boolean;
  onUpload: () => void;
}

const SUPPORTED_FORMATS = 'JPG, PNG, WEBP, PDF';

interface VariantConfig {
  accent: string;
  Icon: ComponentType<SvgIconProps>;
  eyebrow: string;
  title: string;
  subtitle: string;
  cta: string;
  steps: string[];
  isAi: boolean;
}

function useVariantConfig(variant: UploadOptionVariant, isEditMode: boolean): VariantConfig {
  const { t } = useTranslation('goodsReceiving');
  const isAi = variant === 'ai';
  const stepsKey = isAi
    ? isEditMode
      ? 'photoChoice.ai.editSteps'
      : 'photoChoice.ai.steps'
    : 'photoChoice.plain.steps';
  return {
    accent: isAi ? colors.primary.main : colors.text.secondary,
    Icon: isAi ? AutoAwesomeOutlined : AddAPhotoOutlined,
    eyebrow: isAi ? t('photoChoice.ai.eyebrow') : t('photoChoice.plain.eyebrow'),
    title: isAi
      ? isEditMode
        ? t('photoChoice.ai.editTitle')
        : t('photoChoice.ai.title')
      : t('photoChoice.plain.title'),
    subtitle: isAi ? t('photoChoice.ai.subtitle') : t('photoChoice.plain.subtitle'),
    cta: isAi ? t('photoChoice.ai.cta') : t('photoChoice.plain.cta'),
    steps: t(stepsKey, { returnObjects: true }) as string[],
    isAi,
  };
}

export function UploadOptionCard({
  variant,
  isEditMode,
  aiQuotaExhausted = false,
  busy = false,
  onUpload,
}: UploadOptionCardProps) {
  const { t } = useTranslation('goodsReceiving');
  const cfg = useVariantConfig(variant, isEditMode);
  const isDisabled = busy || (cfg.isAi && aiQuotaExhausted);
  const ctaText = cfg.isAi && aiQuotaExhausted ? t('aiExtractionExhausted') : cfg.cta;
  const detailsAriaLabel = t('photoChoice.detailsAriaLabel');
  const [expanded, setExpanded] = useState(false);

  const handleCtaClick = (e: MouseEvent) => {
    e.stopPropagation();
    onUpload();
  };

  return (
    <Paper elevation={0} sx={cardSx(cfg.isAi, cfg.accent)}>
      <Stack spacing={1.25} sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CardHeader cfg={cfg} />
          </Box>
          <IconButton
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={detailsAriaLabel}
            aria-expanded={expanded}
            sx={{
              mt: 0.25,
              color: cfg.isAi ? cfg.accent : 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
        <UploadCta
          isAi={cfg.isAi}
          Icon={cfg.Icon}
          label={ctaText}
          disabled={isDisabled}
          onClick={handleCtaClick}
        />
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Stack spacing={1.5}>
            <StepsList steps={cfg.steps} accent={cfg.accent} />
            {cfg.isAi && (
              <AiDisclaimerBlock
                eyebrow={t('photoChoice.ai.disclaimerEyebrow')}
                body={t('aiDisclaimerItemData')}
              />
            )}
            {cfg.isAi && <ExtractionUsageCounter />}
            <FormatNote
              text={t('photoChoice.formats', {
                formats: SUPPORTED_FORMATS,
                maxSize: MAX_FILE_SIZE_MB,
              })}
            />
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

function cardSx(isAi: boolean, accent: string) {
  return {
    position: 'relative' as const,
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: isAi ? alpha(accent, 0.32) : colors.border.light,
    background: isAi
      ? `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`
      : colors.background.paper,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };
}

function CardHeader({ cfg }: { cfg: VariantConfig }) {
  const { isAi, accent, Icon, eyebrow, title, subtitle } = cfg;
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: alpha(accent, 0.14),
          color: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': { fontSize: '1.35rem' },
        }}
        aria-hidden="true"
      >
        <Icon />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 800,
            color: isAi ? accent : 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontSize: '0.62rem',
            lineHeight: 1.2,
            mb: 0.25,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: '0.98rem' }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.4,
            fontSize: '0.78rem',
            display: 'block',
            mt: 0.25,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function StepsList({ steps, accent }: { steps: string[]; accent: string }) {
  return (
    <Stack spacing={0.75}>
      {steps.map((step, idx) => (
        <Stack
          // biome-ignore lint/suspicious/noArrayIndexKey: static i18n list
          key={`step-${idx}`}
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              mt: '2px',
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: alpha(accent, 0.14),
              color: accent,
              fontSize: '0.66rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden="true"
          >
            {idx + 1}
          </Box>
          <Typography
            variant="body2"
            sx={{ color: 'text.primary', fontSize: '0.82rem', lineHeight: 1.45 }}
          >
            {step}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function FormatNote({ text }: { text: string }) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        alignItems: 'center',
      }}
    >
      <CheckCircleOutlineOutlined sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {text}
      </Typography>
    </Stack>
  );
}

interface UploadCtaProps {
  isAi: boolean;
  Icon: ComponentType<SvgIconProps>;
  label: string;
  disabled: boolean;
  onClick: (e: MouseEvent) => void;
}

function UploadCta({ isAi, Icon, label, disabled, onClick }: UploadCtaProps) {
  return (
    <Button
      variant={isAi ? 'contained' : 'outlined'}
      color={isAi ? 'primary' : 'inherit'}
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      disabled={disabled}
      startIcon={<Icon />}
      fullWidth
      sx={{
        minHeight: 48,
        fontWeight: 700,
        borderRadius: 2,
        textTransform: 'none',
        fontSize: '0.92rem',
        ...(isAi
          ? {}
          : {
              borderColor: colors.border.light,
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
            }),
      }}
    >
      {label}
    </Button>
  );
}
